/**
 * Driver / inverter type catalog — super-admin CRUD page.
 *
 * Maps the integer ``driver_type`` value stored on each
 * ``commandtype`` row to a human-readable label. Backed by
 * ``driver_type_routes.py``:
 *   GET    /api/driver-types
 *   POST   /api/driver-types
 *   PUT    /api/driver-types/{code}
 *   DELETE /api/driver-types/{code}
 *
 * The page lives under Tools → Modbus communication → Driver types.
 */
import { useEffect, useState } from "react";
import {
  Cpu, Plus, TrashSimple, FloppyDisk, ArrowsClockwise,
  WarningCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import AdminPageHeader from "@/components/AdminPageHeader";
import api from "@/lib/api";

const BLANK_ROW = { code: "", label: "" };

export default function ModbusDriverTypesPage({ user, onLogout, onLogoutAll }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [edits, setEdits] = useState({});
  const [newRow, setNewRow] = useState({ ...BLANK_ROW });
  const [savingCode, setSavingCode] = useState(null);
  const [deletingCode, setDeletingCode] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await api.get("/driver-types");
      setRows(r.data?.items || []);
      setEdits({});
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load driver types");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const editField = (code, field, value) =>
    setEdits((cur) => ({ ...cur, [code]: { ...(cur[code] || {}), [field]: value } }));
  const merged = (row) => ({ ...row, ...(edits[row.code] || {}) });
  const isDirty = (row) => {
    const p = edits[row.code];
    if (!p) return false;
    return Object.entries(p).some(([k, v]) => String(row[k] ?? "") !== String(v));
  };

  const saveRow = async (row) => {
    const m = merged(row);
    const codeNum = Number(m.code);
    if (!Number.isFinite(codeNum) || codeNum < 0) {
      toast.error("Code must be a non-negative integer");
      return;
    }
    if (!m.label.trim()) {
      toast.error("Label is required");
      return;
    }
    setSavingCode(row.code);
    try {
      await api.put(`/driver-types/${row.code}`, {
        code: codeNum,
        label: m.label.trim(),
      });
      toast.success(`Saved ${m.label}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingCode(null);
    }
  };

  const deleteRow = async (row) => {
    if (!window.confirm(`Delete driver type ${row.code} (${row.label})?`)) return;
    setDeletingCode(row.code);
    try {
      await api.delete(`/driver-types/${row.code}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeletingCode(null);
    }
  };

  const createRow = async () => {
    const codeNum = Number(newRow.code);
    if (!Number.isFinite(codeNum) || codeNum < 0) {
      toast.error("Code must be a non-negative integer");
      return;
    }
    if (!newRow.label.trim()) {
      toast.error("Label is required");
      return;
    }
    setSavingCode("__new__");
    try {
      await api.post("/driver-types", {
        code: codeNum,
        label: newRow.label.trim(),
      });
      toast.success(`Added ${newRow.label}`);
      setNewRow({ ...BLANK_ROW });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setSavingCode(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminPageHeader
        icon={<Cpu weight="fill" />}
        title="Driver types"
        subtitle="Inverter / driver families referenced by Modbus command types"
        user={user}
        onLogout={onLogout}
        onLogoutAll={onLogoutAll}
      />

      <div className="mx-auto max-w-[800px] px-3 py-3 space-y-3">

        <div className="bg-white border border-slate-200 rounded-sm px-3 py-2 flex items-center gap-2">
          <button
            data-testid="drivertype-refresh"
            onClick={load}
            disabled={loading}
            className="text-[10px] uppercase font-bold px-2 py-1 rounded-sm bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center gap-1 disabled:opacity-50">
            <ArrowsClockwise size={11} weight="bold" /> {loading ? "Loading…" : "Refresh"}
          </button>
          <div className="ml-auto text-[10px] text-slate-500 uppercase tracking-wider font-bold">
            {rows.length} drivers
          </div>
        </div>

        {err && (
          <div data-testid="drivertype-error"
               className="px-3 py-2 bg-red-50 border border-red-200 rounded-sm text-[12px] text-red-800 inline-flex items-center gap-2">
            <WarningCircle size={14} weight="bold" /> {err}
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-[12px]" data-testid="drivertype-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left font-bold w-[120px]">Code</th>
                <th className="px-3 py-2 text-left font-bold">Label</th>
                <th className="px-3 py-2 text-right font-bold w-[140px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-indigo-50/40 border-b border-indigo-100"
                  data-testid="drivertype-new-row">
                <td className="px-3 py-2">
                  <input
                    type="number"
                    data-testid="drivertype-new-code"
                    placeholder="1"
                    value={newRow.code}
                    onChange={(e) => setNewRow({ ...newRow, code: e.target.value })}
                    className="w-full px-2 py-1 rounded-sm border border-slate-300 text-[12px] font-mono"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="text"
                    data-testid="drivertype-new-label"
                    placeholder="EN600-EN500"
                    value={newRow.label}
                    onChange={(e) => setNewRow({ ...newRow, label: e.target.value })}
                    className="w-full px-2 py-1 rounded-sm border border-slate-300 text-[12px]"
                  />
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    data-testid="drivertype-add-btn"
                    onClick={createRow}
                    disabled={savingCode === "__new__" || !newRow.code || !newRow.label}
                    className="inline-flex items-center gap-1 text-[10px] uppercase font-bold px-2 py-1 rounded-sm bg-indigo-700 hover:bg-indigo-800 text-white disabled:opacity-50">
                    <Plus size={11} weight="bold" />
                    {savingCode === "__new__" ? "Adding…" : "Add"}
                  </button>
                </td>
              </tr>

              {loading && rows.length === 0 && (
                <tr><td colSpan={3} className="px-3 py-6 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={3} className="px-3 py-6 text-center text-slate-500">No driver types yet — add one above.</td></tr>
              )}
              {rows.map((row) => {
                const m = merged(row);
                const dirty = isDirty(row);
                return (
                  <tr key={row.code}
                      data-testid={`drivertype-row-${row.code}`}
                      className={`border-b border-slate-100 ${dirty ? "bg-amber-50/40" : "hover:bg-slate-50"}`}>
                    <td className="px-3 py-1.5">
                      <input
                        type="number"
                        data-testid={`drivertype-code-${row.code}`}
                        value={m.code}
                        onChange={(e) => editField(row.code, "code", e.target.value)}
                        className="w-full px-2 py-1 rounded-sm border border-slate-200 text-[12px] font-mono"
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      <input
                        type="text"
                        data-testid={`drivertype-label-${row.code}`}
                        value={m.label}
                        onChange={(e) => editField(row.code, "label", e.target.value)}
                        className="w-full px-2 py-1 rounded-sm border border-slate-200 text-[12px]"
                      />
                    </td>
                    <td className="px-3 py-1.5 text-right whitespace-nowrap">
                      <button
                        data-testid={`drivertype-save-${row.code}`}
                        onClick={() => saveRow(row)}
                        disabled={!dirty || savingCode === row.code}
                        title={dirty ? "Save changes" : "No changes"}
                        className={`inline-flex items-center gap-1 text-[10px] uppercase font-bold px-2 py-1 rounded-sm mr-1 ${
                          dirty
                            ? "bg-emerald-700 hover:bg-emerald-800 text-white"
                            : "bg-slate-100 text-slate-400 cursor-not-allowed"
                        }`}>
                        {savingCode === row.code ? <ArrowsClockwise size={11} weight="bold" className="animate-spin" /> : <FloppyDisk size={11} weight="bold" />}
                        {dirty && <span>Save</span>}
                      </button>
                      <button
                        data-testid={`drivertype-delete-${row.code}`}
                        onClick={() => deleteRow(row)}
                        disabled={deletingCode === row.code}
                        title="Delete row"
                        className="inline-flex items-center gap-1 text-[10px] uppercase font-bold px-2 py-1 rounded-sm bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 disabled:opacity-50">
                        {deletingCode === row.code ? <ArrowsClockwise size={11} weight="bold" className="animate-spin" /> : <TrashSimple size={11} weight="bold" />}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
